import React, { useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Loader2, Sparkles, AlertTriangle, FileText } from 'lucide-react';
import { BRIEFING_SECTIONS } from '../lib/briefingConfig';

const hasValue = (v) => (Array.isArray(v) ? v.length > 0 : Boolean(v) && String(v).trim() !== '');

// Serializa listas/tablas a texto editable (una línea por ítem; tablas "col | col | col")
const toText = (field, v) => {
  if (field.type === 'list') return (v || []).join('\n');
  if (field.type === 'table') return (v || []).map((r) => field.columns.map((c) => r[c] || '').join(' | ')).join('\n');
  return v || '';
};
const fromText = (field, t) => {
  const lines = String(t || '').split('\n').map((l) => l.trim()).filter(Boolean);
  if (field.type === 'list') return lines;
  if (field.type === 'table') return lines.map((l) => { const parts = l.split('|').map((p) => p.trim()); return Object.fromEntries(field.columns.map((c, i) => [c, parts[i] || ''])); });
  return String(t || '').trim();
};

/**
 * Propuesta de importación del briefing: la usuaria acepta, edita o descarta campo a campo.
 * Nunca se guarda nada hasta pulsar "Aplicar".
 */
export function BriefingImportDialog({ open, onClose, result, currentData, onApply }) {
  const rows = useMemo(() => {
    if (!result?.proposal) return [];
    const out = [];
    BRIEFING_SECTIONS.forEach((sec) => {
      const secProp = result.proposal[sec.key] || {};
      sec.fields.forEach((f) => {
        if (!(f.key in secProp)) return;
        const existing = currentData?.[sec.key]?.[f.key];
        const exists = hasValue(existing);
        out.push({ id: `${sec.key}.${f.key}`, sectionKey: sec.key, sectionTitle: sec.title, field: f, proposed: secProp[f.key], existing, exists });
      });
    });
    return out;
  }, [result, currentData]);

  const [edits, setEdits] = useState({});
  const [checked, setChecked] = useState({});
  const [applying, setApplying] = useState(false);

  // Reinicia estado al abrir con una propuesta nueva: existentes DESMARCADOS por defecto (decisión usuaria)
  const resultKey = result?.source_file + ':' + rows.length;
  const [lastKey, setLastKey] = useState(null);
  if (open && lastKey !== resultKey) {
    setLastKey(resultKey);
    setEdits(Object.fromEntries(rows.map((r) => [r.id, toText(r.field, r.proposed)])));
    setChecked(Object.fromEntries(rows.map((r) => [r.id, !r.exists])));
  }

  const selectedCount = rows.filter((r) => checked[r.id]).length;
  const conflicts = rows.filter((r) => r.exists).length;

  const apply = async () => {
    setApplying(true);
    try {
      const bySection = {};
      rows.filter((r) => checked[r.id]).forEach((r) => {
        const val = fromText(r.field, edits[r.id]);
        if (!hasValue(val)) return;
        bySection[r.sectionKey] = { ...(bySection[r.sectionKey] || {}), [r.field.key]: val };
      });
      await onApply(bySection);
      onClose();
    } finally {
      setApplying(false);
    }
  };

  let lastSection = null;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && !applying && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col" data-testid="briefing-import-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Sparkles className="w-5 h-5 text-primary" /> Propuesta de importación del briefing</DialogTitle>
          <DialogDescription className="flex flex-wrap items-center gap-2">
            <FileText className="w-3.5 h-3.5" /> {result?.source_file} · {rows.length} respuestas encontradas
            {conflicts > 0 && (
              <Badge variant="outline" className="border-amber-300 text-amber-700 bg-amber-50" data-testid="briefing-import-conflicts">
                <AlertTriangle className="w-3 h-3 mr-1" /> {conflicts} ya tienen contenido (desmarcadas)
              </Badge>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-3 pr-1" data-testid="briefing-import-rows">
          {rows.length === 0 && <p className="text-sm text-muted-foreground py-6 text-center">No se encontraron respuestas en el documento.</p>}
          {rows.map((r) => {
            const header = r.sectionKey !== lastSection;
            lastSection = r.sectionKey;
            const multiline = r.field.type !== 'text' && r.field.type !== 'select';
            return (
              <React.Fragment key={r.id}>
                {header && <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground pt-2">{r.sectionTitle}</p>}
                <div className={`rounded-lg border p-3 ${checked[r.id] ? 'bg-background' : 'bg-muted/30 opacity-80'}`} data-testid={`briefing-import-row-${r.sectionKey}-${r.field.key}`}>
                  <div className="flex items-start gap-3">
                    <Checkbox checked={!!checked[r.id]} onCheckedChange={(v) => setChecked((c) => ({ ...c, [r.id]: !!v }))} className="mt-1" data-testid={`briefing-import-check-${r.sectionKey}-${r.field.key}`} />
                    <div className="flex-1 min-w-0 space-y-1.5">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-medium">{r.field.label}</p>
                        {r.exists && (
                          <Badge variant="outline" className="border-amber-300 text-amber-700 bg-amber-50 text-[10px]" title={typeof r.existing === 'string' ? r.existing : JSON.stringify(r.existing)}>
                            <AlertTriangle className="w-3 h-3 mr-1" /> Ya tiene contenido · se reemplazará si la marcas
                          </Badge>
                        )}
                      </div>
                      {r.exists && (
                        <p className="text-xs text-muted-foreground line-clamp-2"><span className="font-medium">Actual:</span> {toText(r.field, r.existing)}</p>
                      )}
                      {multiline ? (
                        <Textarea rows={Math.min(6, Math.max(2, String(edits[r.id] || '').split('\n').length))} value={edits[r.id] || ''} onChange={(e) => setEdits((d) => ({ ...d, [r.id]: e.target.value }))} className="text-sm" data-testid={`briefing-import-value-${r.sectionKey}-${r.field.key}`} />
                      ) : (
                        <Input value={edits[r.id] || ''} onChange={(e) => setEdits((d) => ({ ...d, [r.id]: e.target.value }))} className="text-sm h-9" data-testid={`briefing-import-value-${r.sectionKey}-${r.field.key}`} />
                      )}
                      {r.field.type === 'list' && <p className="text-[11px] text-muted-foreground">Un elemento por línea</p>}
                      {r.field.type === 'table' && <p className="text-[11px] text-muted-foreground">Una fila por línea · columnas separadas por "|" ({r.field.columns.join(' | ')})</p>}
                    </div>
                  </div>
                </div>
              </React.Fragment>
            );
          })}
        </div>

        <div className="flex items-center justify-between gap-2 pt-3 border-t">
          <p className="text-xs text-muted-foreground">Solo se guardan los campos marcados. Nada se ha modificado todavía.</p>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} disabled={applying} data-testid="briefing-import-cancel">Descartar</Button>
            <Button onClick={apply} disabled={applying || selectedCount === 0} data-testid="briefing-import-apply">
              {applying ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null} Aplicar {selectedCount} {selectedCount === 1 ? 'campo' : 'campos'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
